import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Clock, MapPin } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import AdirePattern from '../../components/shared/AdirePattern';
import {
  formatWatDate,
  formatWatTime,
  getStatusClasses,
  getVisitActionLabel,
  getVisitAddress,
  getVisitDependantName,
  getVisitStart,
  normalizeVisitStatus,
  sortVisitsOldestFirst,
} from '../../lib/visitUtils';

const watDayKey = (value) => {
  const date = getVisitStart({ scheduledStart: value });
  if (!date) return 'unscheduled';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Africa/Lagos',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
};

const CareOfficerSchedulePage = () => {
  const navigate = useNavigate();
  const { currentUser, visits, dependants, loading, dataError } = useApp();
  const officerId = currentUser?.id || currentUser?.profileId;

  const scheduleDays = useMemo(() => {
    const relevant = sortVisitsOldestFirst(
      visits
        .filter((visit) => !officerId || visit.careOfficerId === officerId)
        .map((visit) => ({
          ...visit,
          dependant: dependants.find((item) => item.id === visit.dependantId),
        }))
    );
    const grouped = relevant.reduce((acc, visit) => {
      const start = getVisitStart(visit);
      const key = start ? watDayKey(start) : 'unscheduled';
      acc[key] = acc[key] || [];
      acc[key].push(visit);
      return acc;
    }, {});
    return Object.entries(grouped).map(([key, dayVisits]) => ({
      key,
      label: key === 'unscheduled' ? 'Unscheduled' : formatWatDate(getVisitStart(dayVisits[0]), { year: 'numeric' }),
      visits: dayVisits,
    }));
  }, [dependants, officerId, visits]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F4EE] px-4 py-10" data-testid="care-officer-schedule">
        <div className="mx-auto max-w-7xl rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-8 text-[#5A5A5A]">Loading schedule...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F4EE]" data-testid="care-officer-schedule">
      <section className="relative overflow-hidden border-b border-[#E8E6E3] bg-[#9F1D35] text-[#FAFAF7]">
        <AdirePattern className="absolute inset-0 h-full w-full text-[#FAFAF7]" opacity={0.12} />
        <div className="relative mx-auto max-w-7xl px-4 py-8 sm:px-6">
          <p className="caption text-[#F5E8EA]">WAT calendar</p>
          <h1 className="mt-2 font-serif text-3xl font-semibold">My Schedule</h1>
          <p className="mt-2 max-w-2xl text-sm text-[#F5E8EA]">All assigned visits grouped by Africa/Lagos time. Scheduled visits stay scheduled until the checklist is submitted.</p>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:py-8">
        {dataError && (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {dataError}
          </div>
        )}

        <div className="grid gap-5 lg:grid-cols-2">
          {scheduleDays.map((day) => (
            <section key={day.key} className="overflow-hidden rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7]">
              <div className="relative overflow-hidden border-b border-[#E8E6E3] bg-[#F5E8EA] px-5 py-4">
                <AdirePattern className="absolute inset-0 h-full w-full text-[#9F1D35]" opacity={0.16} />
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CalendarDays className="h-5 w-5 text-[#9F1D35]" />
                    <h2 className="font-serif text-xl font-semibold text-[#1A1A1A]">{day.label}</h2>
                  </div>
                  <span className="rounded-full bg-[#FAFAF7] px-3 py-1 text-xs font-medium text-[#9F1D35]">{day.visits.length} visits</span>
                </div>
              </div>
              <div className="divide-y divide-[#E8E6E3]">
                {day.visits.map((visit) => (
                  <article key={visit.id} className="p-5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="inline-flex items-center gap-1 text-sm font-medium text-[#9F1D35]">
                            <Clock className="h-4 w-4" />
                            {formatWatTime(getVisitStart(visit))}
                          </span>
                          <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${getStatusClasses(visit.status)}`}>
                            {normalizeVisitStatus(visit.status)}
                          </span>
                        </div>
                        <h3 className="mt-2 font-serif text-xl font-semibold text-[#1A1A1A]">{getVisitDependantName(visit, visit.dependant)}</h3>
                        <p className="mt-2 flex items-start gap-2 text-sm text-[#5A5A5A]">
                          <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0" />
                          {getVisitAddress(visit, visit.dependant)}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => normalizeVisitStatus(visit.status) === 'Scheduled' ? navigate(`/visits/${visit.id}/checklist`) : navigate(`/visits/${visit.id}`)}
                        className="inline-flex justify-center rounded-full bg-[#9F1D35] px-4 py-2 text-sm font-medium text-[#FAFAF7] hover:bg-[#7A1628]"
                      >
                        {getVisitActionLabel(visit)}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>

        {scheduleDays.length === 0 && (
          <div className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-10 text-center text-[#5A5A5A]">
            No scheduled visits yet.
          </div>
        )}
      </main>
    </div>
  );
};

export default CareOfficerSchedulePage;
